/** Live pitch/tempo DSP runs in a Worker. Native Web Audio schedules bounded output chunks. */
export default class BufferedEffects {
  constructor(mixer) {
    this.mixer = mixer;
    this.context = mixer.context;
    this.channels = mixer.channels.filter(channel => channel.buffer);
    this.frames = 8192; // about 186 ms at 44.1 kHz
    this.rendered = 0;
    this.scheduled = 0;
    this.sources = new Set();
    this.closed = false;
    this.settings = { sampleRate: this.context.sampleRate, offset: mixer.offset,
      speed: mixer.speed, pitch: Math.pow(2, mixer.pitchCents / 1200),
      loop: mixer.loop, duration: mixer.duration,
      mobile: typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent) };
    this.worker = new Worker(`${process.env.PUBLIC_URL || ''}/processorScripts/pitch-tempo-worker-v2.js`, { type: 'module' });
    this.worker.onmessage = ({ data }) => {
      const pending = this.pending;
      if (!pending || this.closed) return;
      this.pending = null;
      clearTimeout(pending.timeout);
      if (data.error) pending.reject(new Error(data.error));
      else {
        this.maxProcessingMs = Math.max(this.maxProcessingMs || 0, data.processingMs || 0);
        pending.resolve(data);
      }
    };
    this.worker.onerror = event => this.fail(new Error(event.message || 'Pitch/speed worker failed'));
    this.worker.onmessageerror = () => this.fail(new Error('Pitch/speed audio transfer failed'));
  }

  // Copy only the source window needed for the next output chunk, including FFT preroll.
  // Whole-song buffers stay on the main thread and are never sent to the Worker.
  windows() {
    const { sampleRate: rate, offset, speed, loop, duration } = this.settings;
    const from = Math.floor(offset * rate + this.rendered * speed - 4096 * Math.max(1, speed));
    const to = Math.ceil(offset * rate + (this.rendered + this.frames) * speed + 4096);
    const loopStart = loop ? loop.start * rate : 0;
    const loopEnd = loop ? loop.end * rate : 0;
    const end = duration * rate;
    const sources = this.channels.map(channel => {
      const pcm = [];
      for (let c = 0; c < channel.buffer.numberOfChannels; c++) {
        const original = channel.buffer.getChannelData(c), copy = new Float32Array(to - from);
        for (let i = 0; i < copy.length; i++) {
          let position = from + i;
          if (loop && position >= loopEnd) position = loopStart + (position - loopEnd) % (loopEnd - loopStart);
          if (position < 0 || position >= end || position >= original.length) continue;
          const index = Math.floor(position), fraction = position - index;
          const next = loop && index + 1 >= loopEnd ? Math.floor(loopStart) : index + 1;
          copy[i] = (original[index] || 0) * (1 - fraction) + (original[next] || 0) * fraction;
        }
        pcm.push(copy);
      }
      return { from, pcm };
    });
    return sources;
  }

  request() {
    if (this.closed) return Promise.reject(new Error('Audio processing cancelled'));
    if (this.pending) return Promise.reject(new Error('Overlapping audio processing request'));
    const sources = this.windows();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => this.fail(new Error('Pitch/speed processing timed out')), 15000);
      this.pending = { resolve, reject, timeout };
      this.worker.postMessage({ settings: this.rendered === 0 ? this.settings : null,
        frames: this.frames, sources }, sources.flatMap(source => source.pcm.map(pcm => pcm.buffer)));
    }).then(result => { this.rendered += this.frames; return result; });
  }

  schedule(result) {
    const when = this.startedAt + this.scheduled / this.context.sampleRate;
    // A slow device or backgrounded tab must not create an unbounded catch-up job.
    if (when < this.context.currentTime + .005) throw new Error('Pitch/speed processing could not keep up. Playback has been paused; try returning speed and pitch to normal.');
    result.outputs.forEach((channels, index) => {
      const buffer = this.context.createBuffer(channels.length, this.frames, this.context.sampleRate);
      channels.forEach((pcm, c) => buffer.copyToChannel(pcm, c));
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.channels[index].pan);
      source.onended = () => { source.disconnect(); source.buffer = null; this.sources.delete(source); };
      this.sources.add(source);
      source.start(when);
    });
    this.scheduled += this.frames;
  }

  async start() {
    // A bounded prefix gives mobile JIT/GC and message delivery time to settle.
    const prefix = [];
    for (let i = 0; i < 4; i++) prefix.push(await this.request());
    if (this.closed) throw new Error('Audio processing cancelled');
    this.startedAt = this.context.currentTime + .08;
    prefix.forEach(result => this.schedule(result));
    this.timer = setTimeout(() => this.pump(), 10);
    return this.startedAt;
  }

  async pump() {
    if (this.closed) return;
    try {
      const until = this.startedAt + this.scheduled / this.context.sampleRate;
      if (!this.settings.loop && this.settings.offset + this.scheduled * this.settings.speed / this.context.sampleRate >= this.settings.duration) return;
      if (until - this.context.currentTime < 1.25) {
        const result = await this.request();
        if (this.closed) return;
        this.schedule(result);
      }
      this.timer = setTimeout(() => this.pump(), 20);
    } catch (error) { if (!this.closed) this.fail(error); }
  }

  fail(error) {
    if (this.closed) return;
    this.cancel(error);
    if (this.mixer.effectsSession === this) {
      this.mixer.pause();
      this.mixer.emit({ event: 'error', message: error.message });
    }
  }

  cancel(error = new Error('Audio processing cancelled')) {
    if (this.closed) return;
    this.closed = true;
    clearTimeout(this.timer);
    this.worker.terminate();
    this.worker.onmessage = this.worker.onerror = this.worker.onmessageerror = null;
    if (this.pending) {
      clearTimeout(this.pending.timeout);
      this.pending.reject(error);
      this.pending = null;
    }
    this.sources.forEach(source => {
      source.onended = null;
      try { source.stop(); } catch (ignored) { /* A scheduled source may already have ended. */ }
      source.disconnect(); source.buffer = null;
    });
    this.sources.clear();
  }
}
